import { createContext, useContext, ReactNode, useId } from "react";
import { clsx } from "clsx";

interface FieldContextValue {
  id?: string;
  hasError?: boolean;
  disabled?: boolean;
}

const FieldContext = createContext<FieldContextValue>({});

export const useFieldContext = () => useContext(FieldContext);

interface FieldProps {
  children: ReactNode;
  id?: string;
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
}

function FieldRoot({ children, id: idProp, hasError = false, disabled = false, className }: FieldProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;

  return (
    <FieldContext.Provider value={{ id, hasError, disabled }}>
      <div className={className}>{children}</div>
    </FieldContext.Provider>
  );
}

interface FieldLabelProps {
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}

function FieldLabel({ htmlFor: htmlForProp, children, className }: FieldLabelProps) {
  const { id } = useContext(FieldContext);
  const htmlFor = htmlForProp ?? id;

  return (
    <label htmlFor={htmlFor} className={clsx("block text-sm font-medium text-gray-500", className)}>
      {children}
    </label>
  );
}

interface FieldErrorProps {
  children: ReactNode;
  className?: string;
}

function FieldError({ children, className }: FieldErrorProps) {
  const { hasError } = useContext(FieldContext);

  if (!hasError) {
    return null;
  }

  return <p className={clsx("mt-1 text-xs text-red-600", className)}>{children}</p>;
}

export default Object.assign(FieldRoot, {
  Label: FieldLabel,
  Error: FieldError,
});
